import { MAIN_PLAYER_ID, SILENT_PLAYER_ID } from '@/pages/AdvancedEditor/components/panels/script/TextSection';
import { useUploadActivePageTtsCache } from '@/pages/AdvancedEditor/hooks/useUploadTtsCache';
import { isV3Model } from '@/pages/AdvancedEditor/utils/breakConversion';
import { useAuth } from '@/services/auth';
import { isElevenLabsEnglishVoice } from '@/services/d-id/talks';
import { logger } from '@/services/logger';
import { MAX_TEXT_LENGTH_STREAM } from '@/services/tts/tts-data';
import { concatArrayBuffers, decode, endStream, getReader } from '@/services/tts/tts-data/streamUtils';
import { AudioError } from '@/state/presenters';
import { getDurationFromArrayBuffer } from '@/utils/get-duration-array-buffer';
import { escapeSsmlText } from '@d-id/ssml';
import { IVoice, Providers, VoiceConfig } from '@d-id/types';
import { useCallback, useEffect } from 'react';
import { atom, useRecoilState, useSetRecoilState } from 'recoil';
import useTtsData from '../services/tts/tokenManager';
import { activePlayerIdAtom, PlayerMode, playerModeAtom } from './useAudioPlayer';

interface PlayParams {
    url: string;
    accessToken: string;
    voiceid: string;
    text: string;
    signal: AbortSignal;
    modelId?: string;
    volume?: number;
    speed?: string;
    force?: boolean;
    style?: string;
}

const streamPause = atom<VoidFunction>({
    key: 'stream-pause',
    default: () => {},
});

interface Props {
    voice: IVoice | null;
    text: string;
    voiceConfig: VoiceConfig;
    playerId?: string;
    ttsError?: AudioError | null;
    setTtsError?: (error: AudioError | null) => void;
}

export function useStreamPlayer({ voice, text, voiceConfig, playerId = '', ttsError, setTtsError }: Props) {
    const { accessToken } = useAuth();
    const { data } = useTtsData();

    const [playerMode, setPlayerMode] = useRecoilState(playerModeAtom);
    const setActivePlayerId = useSetRecoilState(activePlayerIdAtom);
    const [pause, setPause] = useRecoilState<VoidFunction>(streamPause);

    const uploadCachedTts = useUploadActivePageTtsCache();

    useEffect(() => {
        if ([SILENT_PLAYER_ID, MAIN_PLAYER_ID].includes(playerId)) {
            setPlayerMode(PlayerMode.NotLoaded);
            setTtsError?.(null);
        }
    }, [text, voice, voiceConfig]);

    const play = useCallback(
        async ({ url, accessToken, voiceid, text, signal, modelId, volume = 1, speed = '1', force = false, style }: PlayParams) => {
            if (!force && playerMode !== PlayerMode.Ready && playerMode !== PlayerMode.NotLoaded) {
                return;
            }

            setPlayerMode(PlayerMode.Loading);
            setActivePlayerId(playerId);
            const allowedText = text.slice(0, MAX_TEXT_LENGTH_STREAM);
            // For v3 models, don't escape text - audio tags like [short pause] should be passed as-is
            const isV3 = isV3Model(modelId);
            const ssmlText = escapeSsmlText(allowedText, true, isV3);
            const reader = await getReader(url, voiceid, ssmlText, accessToken, signal, setTtsError, modelId, speed, style);

            if (reader) {
                const audio = document.createElement('audio');
                const mediaSource = new window.MediaSource();

                audio.src = window.URL.createObjectURL(mediaSource);

                audio.onplaying = () => setPlayerMode(PlayerMode.Playing);
                audio.onended = () => {
                    audio.remove();
                    setPlayerMode(PlayerMode.NotLoaded);
                    setActivePlayerId('');
                };
                mediaSource.onsourceopen = async _ => {
                    const buffers: ArrayBufferLike[] = [];
                    const sourceBuffer = mediaSource.addSourceBuffer('audio/mpeg');
                    let read: Awaited<ReturnType<typeof reader.read>> | null = null;
                    let lastRead: Awaited<ReturnType<typeof reader.read>> | null = null;

                    function appendBuffer() {
                        if (sourceBuffer.updating === false && mediaSource.readyState === 'open') {
                            const currentBuffer = buffers.pop();

                            if (currentBuffer) {
                                sourceBuffer.appendBuffer(currentBuffer as BufferSource);
                            } else if (read?.done) {
                                endStream(mediaSource, lastRead, (e: any) => {
                                    setTtsError?.(e);
                                    setPlayerMode(PlayerMode.Ready);
                                });
                            }
                        }
                    }

                    audio.volume = volume;
                    audio.play();

                    audio.onerror = () => {
                        setPlayerMode(PlayerMode.Ready);
                        setTtsError?.('unknown');
                    };

                    sourceBuffer.addEventListener('updateend', appendBuffer);
                    sourceBuffer.addEventListener('error', e => {
                        if (lastRead) {
                            const decoded = decode(lastRead?.value);

                            if (decoded?.kind) {
                                setTtsError?.(decoded.kind as any);
                            } else {
                                logger.error(e);
                            }
                        }
                    });

                    try {
                        const cacheTtsBuffers = [];
                        do {
                            lastRead = read;
                            read = await reader.read();

                            if (read.value?.buffer) {
                                buffers.push(read.value.buffer);
                                cacheTtsBuffers.push(read.value.buffer);

                                appendBuffer();
                            }
                        } while (read && !read.done);
                        const mergedArrayBuffer = concatArrayBuffers(cacheTtsBuffers);
                        const duration = await getDurationFromArrayBuffer(mergedArrayBuffer);

                        if (playerId && [MAIN_PLAYER_ID, SILENT_PLAYER_ID].includes(playerId) && allowedText.length === text.length) {
                            try {
                                await uploadCachedTts(mergedArrayBuffer, text, duration);
                            } catch {}
                        }
                    } catch (e) {
                        setPlayerMode(PlayerMode.Ready);

                        if (!signal.aborted) {
                            setTtsError?.('unknown');
                            logger.error(`unknown error: ${e}`, { e });
                        }
                    } finally {
                        if (buffers.length === 0 && sourceBuffer.updating === false && mediaSource.readyState === 'open') {
                            endStream(mediaSource, lastRead, (e: any) => {
                                setTtsError?.(e);
                                setPlayerMode(PlayerMode.Ready);
                            });
                        }
                    }
                };

                return audio;
            }
        },
        [uploadCachedTts]
    );

    return {
        playerMode,
        error: ttsError,
        pause,
        play: async (props?: Partial<PlayParams>) => {
            setActivePlayerId(playerId);
            if (data?.streamLambdaUrl && text && voice?.provider === Providers.Elevenlabs) {
                const { streamLambdaUrl } = data;
                const controller = new AbortController();
                const model = voice.config?.modelId || (!isElevenLabsEnglishVoice(voice.locale) ? 'eleven_multilingual_v2' : undefined);
                const player = await play({
                    ...props,
                    url: streamLambdaUrl + Providers.Elevenlabs,
                    accessToken,
                    voiceid: voice.id,
                    text,
                    signal: controller.signal,
                    modelId: model,
                    speed: voiceConfig.rate,
                });

                setPause(() => () => {
                    setPlayerMode(PlayerMode.Ready);
                    controller.abort();
                    player?.pause();
                    player?.remove();
                });
            }
        },
        playSilent: async (options?: { force?: boolean }) => {
            if (data?.streamLambdaUrl && text && voice?.provider === Providers.Elevenlabs) {
                const { streamLambdaUrl } = data;
                const controller = new AbortController();
                const model = voice.config?.modelId || (!isElevenLabsEnglishVoice(voice.locale) ? 'eleven_multilingual_v2' : undefined);
                await play({
                    url: streamLambdaUrl + Providers.Elevenlabs,
                    accessToken,
                    voiceid: voice.id,
                    text,
                    signal: controller.signal,
                    modelId: model,
                    volume: 0,
                    speed: voiceConfig.rate,
                    force: options?.force,
                });
            }
        },
    };
}
